import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircleQuestionMark } from 'lucide-react';

import PageHeaderActions from '../../components/PageHeaderActions';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { listMySupportRequests } from '../../services/api';
import {
  SUPPORT_STATUS_LABELS,
  SUPPORT_STATUS_VARIANTS,
  supportTypeLabel,
  formatSupportDate,
} from './support-display';

/**
 * Every support request raised by anyone in the signed-in user's organisation.
 *
 * Organisation-wide rather than per-user, as the card asks: a shop's requests are the shop's
 * business, and someone covering for a colleague should not have to re-raise a question that
 * has already been answered. Nothing here filters by organisation for safety — row
 * permissions already make another shop's tickets unreadable.
 */
export default function Support() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRequests(await listMySupportRequests());
      setError('');
    } catch (loadError) {
      setError(loadError?.message || 'Your support requests could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold">Support</h1>
        <PageHeaderActions />
      </div>

      {loading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-20 w-full" />
          ))}
        </div>
      )}

      {!loading && error && (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {!loading && !error && requests.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 p-12 text-center">
            <MessageCircleQuestionMark className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="font-medium">No support requests yet</p>
              <p className="text-sm text-muted-foreground">
                Use the support icon in the header of any page to raise one.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!loading && !error && requests.length > 0 && (
        <div className="space-y-3">
          {requests.map((request) => (
            <Card
              key={request.$id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/support/${request.$id}`)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/support/${request.$id}`);
                }
              }}
              className="cursor-pointer transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <CardContent className="flex items-start justify-between gap-4 p-4">
                <div className="min-w-0 space-y-1">
                  <p className="truncate font-medium">{request.subject}</p>
                  <p className="text-sm text-muted-foreground">
                    {request.reference} · {supportTypeLabel(request.type)} · raised{' '}
                    {formatSupportDate(request.$createdAt)}
                  </p>
                </div>
                <Badge variant={SUPPORT_STATUS_VARIANTS[request.status] || 'secondary'}>
                  {SUPPORT_STATUS_LABELS[request.status] || request.status}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
